import cn from "@/v2-ui/utils/utils.cn"
import Tabs, { useTabParam } from "@/v2-backoffice-client/components/tabs"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import LayerTable from "@/v2-backoffice-client/layer/LayerTable"
import LayerPageActions from "@/v2-backoffice-client/layer/LayerPageActions"
import LayerCategoryTable from "@/v2-backoffice-client/layer/category/LayerCategoryTable"
import LayerReviewTable from "@/v2-backoffice-client/layer/review/LayerReviewTable"
import LayerRejectedTable from "@/v2-backoffice-client/layer/review/LayerRejectedTable"
import LayerUnseededBanner from "@/v2-backoffice-client/layer/authoring/LayerUnseededBanner"
import BasemapTable from "@/v2-backoffice-client/basemap/BasemapTable"
import NavLayerTable from "@/v2-backoffice-client/navlayer/NavLayerTable"
import {
  LAYER_PAGE_DEFAULT_TAB,
  LAYER_PAGE_TAB,
  LAYER_PAGE_TABS_ID,
  toLayerPageTab,
} from "@/v2-backoffice-client/layer/layer.constants"
import { useLayerCountsQuery } from "@/v2-backoffice-client/layer/counts/layer.counts.query"

export type LayerPageProps = PageProps

const TAB_CONTENT_CLASSES = "flex flex-1 min-h-0 overflow-auto"

/**
 * Everything the map is made of, in one page: the three catalogs an
 * operator edits (map layers, basemaps, nav layers) plus the categories
 * that group them, then — after a separator — the review states a map layer
 * moves through on its way into the catalog.
 */
function LayerPage(props: LayerPageProps) {
  const { className, ...pageProps } = props

  const { data, loading } = useLayerCountsQuery()
  const counts = data?.getLayerCounts
  const [tab] = useTabParam(LAYER_PAGE_TABS_ID, LAYER_PAGE_DEFAULT_TAB)

  return (
    <Page className={cn("layers-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title>Layers</Page.Title>

        <Page.Header.Actions>
          <LayerPageActions tab={toLayerPageTab(tab)} />
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 overflow-auto">
        <Tabs
          id={LAYER_PAGE_TABS_ID}
          defaultValue={LAYER_PAGE_DEFAULT_TAB}
          variant="sidebar"
        >
          <Tabs.List>
            <Tabs.Trigger
              value={LAYER_PAGE_TAB.layers}
              iconSrc="layers"
              isLoading={loading}
              count={counts?.Total}
            >
              Map layers
            </Tabs.Trigger>
            <Tabs.Trigger
              value={LAYER_PAGE_TAB.basemaps}
              iconSrc="map"
              isLoading={loading}
              count={counts?.Basemaps}
            >
              Basemaps
            </Tabs.Trigger>
            <Tabs.Trigger
              value={LAYER_PAGE_TAB.navLayers}
              iconSrc="compass-alt"
              isLoading={loading}
              count={counts?.NavLayers}
            >
              Nav layers
            </Tabs.Trigger>
            <Tabs.Trigger
              value={LAYER_PAGE_TAB.categories}
              iconSrc="folder"
              isLoading={loading}
              count={counts?.Categories}
            >
              Categories
            </Tabs.Trigger>

            {/* Catalogs above, the map layer's review states below: they
                are stages of one catalog, not catalogs. */}
            <Tabs.Separator />

            <Tabs.Trigger
              value={LAYER_PAGE_TAB.review}
              iconSrc="eye"
              isLoading={loading}
              count={counts?.PendingReview}
            >
              Review queue
            </Tabs.Trigger>
            <Tabs.Trigger
              value={LAYER_PAGE_TAB.rejected}
              iconSrc="ban"
              isLoading={loading}
              count={counts?.Rejected}
            >
              Rejected
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content
            value={LAYER_PAGE_TAB.layers}
            className="flex flex-col flex-1 min-h-0 overflow-auto"
          >
            <LayerUnseededBanner className="mb-2 shrink-0" />
            <LayerTable />
          </Tabs.Content>

          <Tabs.Content
            value={LAYER_PAGE_TAB.basemaps}
            className={TAB_CONTENT_CLASSES}
          >
            <BasemapTable />
          </Tabs.Content>

          <Tabs.Content
            value={LAYER_PAGE_TAB.navLayers}
            className={TAB_CONTENT_CLASSES}
          >
            <NavLayerTable />
          </Tabs.Content>

          <Tabs.Content
            value={LAYER_PAGE_TAB.categories}
            className={TAB_CONTENT_CLASSES}
          >
            <LayerCategoryTable />
          </Tabs.Content>

          <Tabs.Content
            value={LAYER_PAGE_TAB.review}
            className={TAB_CONTENT_CLASSES}
          >
            <LayerReviewTable />
          </Tabs.Content>

          <Tabs.Content
            value={LAYER_PAGE_TAB.rejected}
            className={TAB_CONTENT_CLASSES}
          >
            <LayerRejectedTable />
          </Tabs.Content>
        </Tabs>
      </Page.Content>
    </Page>
  )
}

export default LayerPage
